// frontend/src/stores/authStore.js
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import api, {
  getCurrentUser,
  requestLoginCode as apiRequestLoginCode,
  verifyLoginCode as apiVerifyLoginCode,
} from "../lib/api";
import router from "../router";

export const useAuthStore = defineStore("auth", () => {
  // State
  const currentUser = ref(null);
  const isAuthenticated = ref(false);
  const isLoading = ref(false);
  const authError = ref(null);

  // Computed properties
  const isAdmin = computed(
    () => isAuthenticated.value && currentUser.value?.role === "admin"
  );

  const isRegularUser = computed(
    () => isAuthenticated.value && currentUser.value?.role === "user"
  );

  // Initialize auth from localStorage on app start
  const initializeAuth = async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      resetAuth();
      return false;
    }

    try {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      // Fetch current user details
      isLoading.value = true;
      const response = await getCurrentUser();
      currentUser.value = response.data;
      isAuthenticated.value = true;
      authError.value = null;
      return true;
    } catch (error) {
      console.error("Auth initialization error:", error);
      resetAuth();
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  // Login flow: Request code
  const requestLoginCode = async (email) => {
    isLoading.value = true;
    authError.value = null;

    try {
      await apiRequestLoginCode(email);
      return true;
    } catch (error) {
      console.error("Login request error:", error);
      authError.value = "Failed to send login code. Please try again.";
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  // Login flow: Verify code
  const verifyLoginCode = async (email, code) => {
    isLoading.value = true;
    authError.value = null;

    try {
      const response = await apiVerifyLoginCode(email, code);

      // Store token
      const token = response.data.access_token;
      localStorage.setItem("auth_token", token);

      // Set up axios authorization header
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      // Fetch user details
      const userResponse = await getCurrentUser();
      currentUser.value = userResponse.data;
      isAuthenticated.value = true;

      return true;
    } catch (error) {
      console.error("Code verification error:", error);
      authError.value = "Invalid login code. Please try again.";
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  // Logout
  const logout = () => {
    resetAuth();
    router.push("/login");
  };

  // Reset auth state
  const resetAuth = () => {
    localStorage.removeItem("auth_token");
    delete api.defaults.headers.common["Authorization"];
    currentUser.value = null;
    isAuthenticated.value = false;
  };

  return {
    // State
    currentUser,
    isAuthenticated,
    isAdmin,
    isRegularUser,
    isLoading,
    authError,

    // Methods
    initializeAuth,
    requestLoginCode,
    verifyLoginCode,
    logout,
    resetAuth,
  };
});
